import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import MainLayout from '../../layouts/MainLayout';

// Format the date as dd/mm/yyyy
const formatDate = (date) => {
  const jsDate = new Date(date);
  const day = jsDate.getDate().toString().padStart(2, '0');
  const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
  const year = jsDate.getFullYear();
  return `${day}/${month}/${year}`;
};

// Format the time as hh:mm:ss
const formatTime = (date) => {
  const jsDate = new Date(date);
  const hours = jsDate.getHours().toString().padStart(2, '0');
  const minutes = jsDate.getMinutes().toString().padStart(2, '0');
  const seconds = jsDate.getSeconds().toString().padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};

// Format the date and time as dd/mm/yyyy hr:min:sec
const formatDateTime = (date) => {
  const jsDate = new Date(date);
  const day = jsDate.getDate().toString().padStart(2, '0');
  const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
  const year = jsDate.getFullYear();
  const hours = jsDate.getHours().toString().padStart(2, '0');
  const minutes = jsDate.getMinutes().toString().padStart(2, '0');
  const seconds = jsDate.getSeconds().toString().padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

// Check if a value is a valid date
const isDate = (value) => {
  const date = new Date(value);
  return !isNaN(date.getTime());
};

const IntegraViewData = () => {
  const authState = useSelector(state => state.authReducer);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');
  const [fetchData, { loading }] = useFetch();

  const fetchIntegraDataLog = useCallback(() => {
    const config = { 
      url: "/integradatalog", 
      method: "get", 
      headers: { Authorization: authState.token },
      data: { deviceId: 'ABC' }
    };
    
    fetchData(config, { showSuccessToast: false })
      .then(({ logs }) => setLogs(logs))
      .catch(error => setError(error));
  }, [authState.token, fetchData]);

  useEffect(() => {
    if (authState.isLoggedIn) {
      fetchIntegraDataLog();
    }
  }, [authState.isLoggedIn, fetchIntegraDataLog]);

  const sortedLogs = [...logs].sort((a, b) => new Date(b.logDate) - new Date(a.logDate));

  const groupedLogs = sortedLogs.reduce((acc, log) => {
    const logDate = formatDate(log.logDate);
    if (!acc[logDate]) {
      acc[logDate] = [];
    }
    acc[logDate].push(log);
    return acc;
  }, {});

  // Extract additional details from the first log if available
  const extractDetails = (logs) => {
    if (logs.length > 0) {
      const firstLog = logs[0];
      return {
        deviceCode: firstLog.deviceCode,
        locationCode: firstLog.locationCode,
        ownerCode: firstLog.ownerCode,
        ownerName: firstLog.ownerName,
        userCode: firstLog.userCode,
        userName: firstLog.userName
      };
    }
    return {};
  };

  const details = extractDetails(logs);

  // Determine if a log entry is abnormal
  const isAbnormal = (entry) => {
    // Replace this logic with actual conditions for abnormal data
    return entry.logValue.includes('Abnormal'); // Example condition
  };


  return (
    <MainLayout>
      <div className="mt-4 mx-auto px-4 py-6">
        <h1 className="text-3xl font-bold mb-4 text-yellow-200">Smart Diaper Logs</h1>

        {loading && <p>Loading...</p>}
        {error && <p className="text-red-500">Error: {error.message}</p>}

        {/* Display additional log details */}
        <div className="mb-6 p-4 bg-gray-800 text-white rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-2">Log Details For</h2>
          <ul>
            <li><strong>Device Code:</strong> {details.deviceCode}</li>
            <li><strong>Location Code:</strong> {details.locationCode}</li>
            <li><strong>Owner Code:</strong> {details.ownerCode}</li>
            <li><strong>Owner Name:</strong> {details.ownerName}</li>
            <li><strong>User Code:</strong> {details.userCode}</li>
            <li><strong>User Name:</strong> {details.userName}</li>
          </ul>
        </div>

        {logs.length === 0 ? (
          <p>No logs available.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-gray-600 border border-gray-200 rounded-lg shadow-md">
              <thead>
                <tr className="bg-gray-600 text-white border-b border-gray-200">
                  <th className="py-3 px-4 text-left">Date</th>
                  <th className="py-3 px-4 text-left">Key</th>
                  <th className="py-3 px-4 text-left">Value</th>
                  <th className="py-3 px-4 text-left">Unit</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedLogs).map(([date, logsByDate], dateIndex) => (
                  <React.Fragment key={dateIndex}>
                    <tr>
                      <td colSpan="4" className="py-2 px-4 bg-blue-500 text-lg font-semibold border-b border-gray-200">
                        {date}
                      </td>
                    </tr>
                    {logsByDate.map((log, logIndex) => (
                      <React.Fragment key={logIndex}>
                        {log.logs.map((entry, entryIndex) => (
                          <tr key={entryIndex} className={isAbnormal(entry) ? "bg-red-500 text-white" : "hover:bg-gray-500"}>
                            {entryIndex === 0 && (
                              <>
                                <td className="py-2 px-4 border-b border-gray-200">
                                  {formatTime(log.logDate)}
                                </td>
                                <td className="py-2 px-4 border-b border-gray-200">
                                  {entry.logKey}
                                </td>
                                <td className="py-2 px-4 border-b border-gray-200">
                                  {entry.logValue}
                                </td>
                                <td className="py-2 px-4 border-b border-gray-200">
                                  {entry.logUnit}
                                </td>
                              </>
                            )}
                            {(entryIndex > 0 && entryIndex < 3) && (
                              <>
                                <td className="py-2 px-4 border-b border-gray-200"></td>
                                <td className="py-2 px-4 border-b border-gray-200">
                                  {entry.logKey}
                                </td>
                                <td className="py-2 px-4 border-b border-gray-200">
                                  {entry.logValue}
                                </td>
                                <td className="py-2 px-4 border-b border-gray-200">
                                  {entry.logUnit}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default IntegraViewData;
